import { useState, useEffect } from "react";
import { API , authFetch } from "@/App";
import { toast } from "sonner";
import {
  MapPin,
  Clock,
  User,
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  Satellite,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import LiveTrackingMap from "@/components/LiveTrackingMap";
import AttendanceCalendar from "@/components/AttendanceCalendar";

const AttendancePage = () => {
  const [attendance, setAttendance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchData();
  }, [selectedDate, statusFilter]);

  const fetchData = async () => {
    try {
      // Fetch employees
      const empResponse = await authFetch(`${API}/employees?status=ACTIVE`, {
        
      });
      const empData = await empResponse.json();
      if (empData.success) {
        setEmployees(empData.data);
      }

      // Fetch attendance
      let url = `${API}/attendance?date=${selectedDate}`;
      if (statusFilter && statusFilter !== "all") url += `&status=${statusFilter}`;

      const attResponse = await authFetch(url, {  });
      const attData = await attResponse.json();
      if (attData.success) {
        setAttendance(attData.data);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      PRESENT: { icon: CheckCircle, class: "badge-success", label: "Present" },
      ABSENT: { icon: XCircle, class: "badge-danger", label: "Absent" },
      LATE: { icon: AlertCircle, class: "badge-warning", label: "Late" },
      HALF_DAY: { icon: Clock, class: "badge-info", label: "Half Day" },
    };
    const config = styles[status] || styles.ABSENT;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.class}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  // Calculate stats
  const presentCount = attendance.filter((a) => a.status === "PRESENT" || a.status === "LATE").length;
  const absentCount = employees.length - presentCount;
  const lateCount = attendance.filter((a) => a.status === "LATE").length;

  return (
    <div className="space-y-6" data-testid="attendance-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">
          Attendance & GPS (Punch In / Punch Out)
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Daily employee <strong>punch-in / punch-out</strong> and live location. For individual dealer visit check-ins, see the Dealer Visits section.
        </p>
      </div>

      {/* View Tabs */}
      <Tabs defaultValue="daily" className="w-full">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="daily" data-testid="tab-daily-attendance">Daily View</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-monthly-calendar">Monthly Calendar</TabsTrigger>
        </TabsList>

        <TabsContent value="daily" className="space-y-6 mt-4">
        {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4" data-testid="stat-present">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-100">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 font-mono">{presentCount}</p>
              <p className="text-sm text-slate-500">Present Today</p>
            </div>
          </div>
        </Card>
        <Card className="p-4" data-testid="stat-absent">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-100">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 font-mono">{absentCount}</p>
              <p className="text-sm text-slate-500">Absent Today</p>
            </div>
          </div>
        </Card>
        <Card className="p-4" data-testid="stat-late">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 font-mono">{lateCount}</p>
              <p className="text-sm text-slate-500">Late Punch-In</p>
            </div>
          </div>
        </Card>
        <Card className="p-4" data-testid="stat-total">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 font-mono">{employees.length}</p>
              <p className="text-sm text-slate-500">Total Employees</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-40"
              data-testid="date-picker"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="PRESENT">Present</SelectItem>
              <SelectItem value="ABSENT">Absent</SelectItem>
              <SelectItem value="LATE">Late</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Simulate GPS Button + Live Tracking Map */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Satellite className="w-4 h-4 text-[#8B1A1A]" />
            Live Location Map
          </h3>
          <button
            onClick={async () => {
              try {
                const res = await authFetch(`${API}/gps/simulate`, { method: "POST",  });
                const data = await res.json();
                if (data.success) {
                  toast.success(data.message);
                  fetchData();
                }
              } catch (err) {
                toast.error("Failed to simulate GPS data");
              }
            }}
            className="px-3 py-1.5 text-xs font-medium bg-[#8B1A1A] text-white rounded-lg hover:bg-[#6B1515] transition-colors"
            data-testid="simulate-gps-btn"
          >
            Simulate GPS Data
          </button>
        </div>
        <LiveTrackingMap />
      </Card>

      {/* Attendance Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="attendance-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Punch-In</th>
                <th>Punch-Out</th>
                <th>Hours on Field</th>
                <th>Status</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-8">
                    <div className="w-6 h-6 spinner mx-auto"></div>
                  </td>
                </tr>
              ) : employees.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-slate-500">
                    No employees found
                  </td>
                </tr>
              ) : (
                employees.map((employee) => {
                  const att = attendance.find(
                    (a) => a.employee_id === employee.employee_id
                  );
                  return (
                    <tr key={employee.employee_id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#8B1A1A]/10 flex items-center justify-center">
                            <span className="text-[#8B1A1A] font-medium text-xs">
                              {employee.full_name?.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 text-sm">
                              {employee.full_name}
                            </p>
                            <p className="text-xs text-slate-500">
                              {employee.employee_code}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="font-mono text-sm">
                        {att?.check_in_time || "-"}
                      </td>
                      <td className="font-mono text-sm">
                        {att?.check_out_time || "-"}
                      </td>
                      <td className="font-mono text-sm">
                        {att?.hours_on_field ? `${att.hours_on_field}h` : "-"}
                      </td>
                      <td>{getStatusBadge(att?.status || "ABSENT")}</td>
                      <td>
                        {att?.check_in_location ? (
                          <span className="text-xs text-slate-500">
                            GPS recorded
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
        </TabsContent>

        <TabsContent value="calendar" className="mt-4">
          <AttendanceCalendar employees={employees} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AttendancePage;
